import { ApiError, Insurer, InsurersService } from '@/@types/generated'
import Loading from '@/components/Atoms/Loading'
import { useError } from '@/hooks/useError'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

interface InsurerContextProps {
  readonly insurer: Insurer
  reload: (background?: boolean) => Promise<void>
}

const InsurerContext = createContext<InsurerContextProps>(undefined!)

export function useInsurer() {
  const context = useContext(InsurerContext)
  if (context === undefined) {
    throw new Error('useInsurer must be used within an InsurerProvider route')
  }
  return context
}

export function InsurerProvider({
  licenseNumber,
  children,
  onError,
}: React.PropsWithChildren<{
  licenseNumber: number
  onError?: (error: unknown) => void
}>) {
  const { showError } = useError()
  const [loading, setLoading] = useState(true)
  const [insurer, setInsurer] = useState<Insurer | null>(null)

  const reload = useCallback(
    async (background?: boolean) => {
      if (!background) setLoading(true)
      try {
        const data = await InsurersService.getInsurer(licenseNumber)
        setInsurer(data)
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          showError('Insurer not found')
        }
        onError?.(error)
      } finally {
        setLoading(false)
      }
    },
    [licenseNumber, onError, showError]
  )

  useEffect(() => {
    reload()
  }, [reload])

  if (loading)
    return (
      <Loading variant="fullscreen" message="Retrieving insurer details..." />
    )
  if (!insurer) return <div>Insurer not found</div>

  return (
    <InsurerContext.Provider value={{ insurer, reload }}>
      {children}
    </InsurerContext.Provider>
  )
}
